import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import { Sparkles, ArrowLeft, Lock } from 'lucide-react';

// Friendly placeholder for IA slots that aren't yet enabled on this subscription.
// IMPORTANT: NO internal language ("iteration", "phase", "roadmap") is ever shown
// to the user. The message is calm, professional, and subscription-framed.
const TITLES = {
  'occupancy': { en: 'Occupancy intelligence', ar: 'ذكاء الإشغال' },
  'asset-health': { en: 'Asset health', ar: 'صحّة الأصول' },
  'property-owners': { en: 'Property owners', ar: 'مُلّاك العقارات' },
  'outstanding-balances': { en: 'Outstanding balances', ar: 'الأرصدة المستحقّة' },
  'portfolio-benchmark': { en: 'Portfolio benchmarking', ar: 'مقارنة المحفظة' },
  'draft-leases': { en: 'Draft leases', ar: 'عقود قيد الإعداد' },
  'move-in-out': { en: 'Move-In / Move-Out', ar: 'الإشغال والإخلاء' },
  'tenant-risk': { en: 'Tenant risk scoring', ar: 'تقييم مخاطر المستأجر' },
  'budgets': { en: 'Budgets', ar: 'الموازنات' },
  'expenses': { en: 'Expenses', ar: 'المصروفات' },
  'owner-statements': { en: 'Owner statements', ar: 'كشوف الملّاك' },
  'banking': { en: 'Banking', ar: 'الخدمات البنكيّة' },
  'chart-of-accounts': { en: 'Chart of accounts', ar: 'دليل الحسابات' },
  'investor-reporting': { en: 'Investor reporting', ar: 'تقارير المستثمرين' },
  'zatca-invoices': { en: 'ZATCA invoicing', ar: 'فواتير زاتكا' },
  'preventive': { en: 'Preventive maintenance', ar: 'صيانة وقائيّة' },
  'sla-tracking': { en: 'SLA tracking', ar: 'تتبّع اتفاقيّات الخدمة' },
  'asset-condition': { en: 'Asset condition', ar: 'حالة الأصل' },
  'maintenance-analytics': { en: 'Maintenance analytics', ar: 'تحليلات الصيانة' },
  'recurring-tasks': { en: 'Recurring tasks', ar: 'مهام متكرّرة' },
  'automation': { en: 'Automation rules', ar: 'قواعد الأتمتة' },
  'email': { en: 'Email center', ar: 'البريد الإلكتروني' },
  'whatsapp': { en: 'WhatsApp Business', ar: 'واتساب' },
  'sms': { en: 'SMS', ar: 'الرسائل النصيّة' },
  'templates': { en: 'Message templates', ar: 'القوالب' },
  'notifications': { en: 'Notification center', ar: 'الإشعارات' },
  'calendar': { en: 'Operations calendar', ar: 'تقويم العمليات' },
  'sop-readiness': { en: 'SOP readiness', ar: 'جاهزيّة الإجراءات' },
  'snagging': { en: 'Snagging register', ar: 'الملاحظات الفنّيّة' },
  'staffing': { en: 'Staffing readiness', ar: 'جاهزيّة الفريق' },
  'maintenance-readiness': { en: 'Maintenance readiness', ar: 'جاهزيّة الصيانة' },
  'opening': { en: 'Opening readiness', ar: 'جاهزيّة الافتتاح' },
};

export default function ComingSoon() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const key = (slug || '').split('/')[0];
  const t = TITLES[key] || { en: 'Available soon', ar: 'متوفّر قريبًا' };
  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="coming-soon-page">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[12px] text-[#71777E] hover:text-[#0E1320] mb-6">
          <ArrowLeft size={14} /> {isAr ? 'رجوع' : 'Back'}
        </button>
        <div className="rounded-2xl border border-[#E6EAF0] bg-white p-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[rgba(0,168,107,0.08)] border border-[rgba(0,168,107,0.20)] flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-[color:var(--reva-signal)]" strokeWidth={1.5} />
          </div>
          <h1 className="mt-5 text-2xl font-semibold tracking-tight text-[#0E1320]" data-testid="coming-soon-title">
            {isAr ? t.ar : t.en}
          </h1>
          <p className="mt-3 text-[14px] text-[#4A5263] max-w-md mx-auto leading-relaxed">
            {isAr
              ? 'هذه القدرة ستتوفّر في اشتراكك قريبًا. تواصل مع فريق REVA لتفعيلها أو لترقية باقتك.'
              : 'This capability will be available in your subscription soon. Contact your REVA account team to enable it or upgrade your plan.'}
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
            <button onClick={() => navigate('/dashboard')} className="reva-btn-emerald-l text-[13px] py-2 px-4" data-testid="coming-soon-dashboard">
              {isAr ? 'العودة إلى لوحة القيادة' : 'Back to dashboard'}
            </button>
            <button onClick={() => navigate('/settings')} className="reva-btn-ghost-l text-[13px] py-2 px-4 inline-flex items-center gap-1.5" data-testid="coming-soon-settings">
              <Lock size={14} /> {isAr ? 'الاشتراك' : 'My subscription'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
